import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import TranslateIcon from "@mui/icons-material/Translate";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useContext, useState } from "react";
import AuthContext from "../context/AuthContext";
import { useTranslation } from "react-i18next";

export default function Navbar({ handleDrawerToggle, menuButtonRef }) {
  const { user, logout } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  
  const [langEl, setLangEl] = useState(null);
  const [moreEl, setMoreEl] = useState(null);
  
  const handleLanguageMenu = (e) => setLangEl(e.currentTarget);
  const handleLanguageChange = (lng) => {
      i18n.changeLanguage(lng);
      setLangEl(null);
  };
  
  const defocusAnywhere = () => {
      // If focus is sitting inside a (closed) Drawer, blur it
      const active = document.activeElement;
      if (active && active.closest('.MuiDrawer-root')) {
          active.blur();
      } else {
          // otherwise just blur the currently focused control
          document.activeElement?.blur?.();
      }
  };

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
      elevation={1}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        {/* Left: menu + title */}
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <IconButton
            ref={menuButtonRef}
            color="inherit"
            edge="start"
            onClick={(e) => { e.currentTarget.blur(); handleDrawerToggle(); }}
            sx={{ mr: 1 }}
            aria-label={t("open_menu")}
          >
            <MenuIcon />
          </IconButton>

          {/* Title shrinks on phones */}
          <Typography
            variant="h6"
            noWrap
            sx={{
              maxWidth: { xs: 170, sm: "unset" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={t("pig_counter_system")}
          >
            {t("pig_counter_system")}
          </Typography>
        </Box>

        {/* Right: client + actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            gap: 1,
          }}
        >
          {/* Client name (truncate on phones) */}
          {user?.client?.name && (
            <Typography
              variant="body2"
              sx={{
                display: { xs: "none", sm: "block" }, // hide on phones to save space
                color: "inherit",
                opacity: 0.9,
              }}
              title={`${t("client")}: ${user.client.name}`}
            >
              {t("client")}: {user.client.name}
            </Typography>
          )}

          {/* Desktop / tablet actions */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
            <IconButton
            color="inherit"
            onClick={(e) => { defocusAnywhere(); handleLanguageMenu(e); }}
            aria-label={t("language")}
            >
              <TranslateIcon />
            </IconButton>
            <Menu anchorEl={langEl} open={Boolean(langEl)} onClose={() => setLangEl(null)}>
              <MenuItem onClick={() => handleLanguageChange("en")}>🇬🇧 English</MenuItem>
              <MenuItem onClick={() => handleLanguageChange("es")}>🇪🇸 Español</MenuItem>
            </Menu>

            <Button color="inherit" onClick={() => { defocusAnywhere(); logout(); }} sx={{ ml: 1 }}>
              {t("logout")}
            </Button>
          </Box>

          {/* Phone: collapse into “More” menu */}
          <Box sx={{ display: { xs: "flex", sm: "none" } }}>
            <IconButton
              color="inherit"
              onClick={(e) => { defocusAnywhere(); setMoreEl(e.currentTarget); }}
              aria-label={t("more_options")}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={moreEl}
              open={Boolean(moreEl)}
              onClose={() => setMoreEl(null)}
              keepMounted
            >
              <MenuItem onClick={handleLanguageMenu}>
                <TranslateIcon fontSize="small" style={{ marginRight: 8 }} />
                {t("language")}
              </MenuItem>
              <MenuItem onClick={logout}>{t("logout")}</MenuItem>
            </Menu>

            {/* Nested language menu (opens when selecting Language) */}
            <Menu
              anchorEl={langEl}
              open={Boolean(langEl)}
              onClose={() => setLangEl(null)}
              keepMounted
            >
              <MenuItem onClick={() => handleLanguageChange("en")}>🇬🇧 English</MenuItem>
              <MenuItem onClick={() => handleLanguageChange("es")}>🇪🇸 Español</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}